import { useCallback, useEffect, useState } from "react";
import api, { errorMessage } from "../lib/api";
import { GhostButton, ActionButton } from "./Primitives";
import { toast } from "sonner";
import { Mail, Send, Plus, Trash2, Calendar, Repeat } from "lucide-react";

// EmailStr validation is intentionally loose here — the backend also
// validates via Pydantic. We just guard against blatantly invalid rows.
function isEmail(v) {
  return typeof v === "string" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}

// Cadence options — labels + when-copy for the picker card.
const CADENCE_OPTIONS = [
  { value: "weekly",   label: "Weekly",     when: "Every configured day" },
  { value: "biweekly", label: "Bi-weekly",  when: "Every other configured day" },
  { value: "monthly",  label: "Monthly",    when: "1st configured day of month" },
];

const DAY_OPTIONS = [
  { value: 0, label: "Monday" },
  { value: 1, label: "Tuesday" },
  { value: 2, label: "Wednesday" },
  { value: 3, label: "Thursday" },
  { value: 4, label: "Friday" },
  { value: 5, label: "Saturday" },
  { value: 6, label: "Sunday" },
];

// Given a UTC hour (0-23), return "HH:00 (local TZ)" for the current browser.
function localTimeForUtcHour(utcHour) {
  try {
    const now = new Date();
    const utc = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), utcHour, 0, 0));
    const parts = new Intl.DateTimeFormat(undefined, {
      hour: "2-digit", minute: "2-digit", timeZoneName: "short", hour12: false,
    }).format(utc);
    return parts;
  } catch {
    return `${String(utcHour).padStart(2, "0")}:00 UTC`;
  }
}

function Toggle({ enabled, onChange, testId }) {
  return (
    <button
      onClick={() => onChange(!enabled)}
      data-testid={testId}
      className={`relative inline-flex h-6 w-11 items-center transition-colors border ${
        enabled ? "bg-[#FF3B30] border-[#FF3B30]" : "bg-[#141414] border-[#27272A]"
      }`}
      aria-checked={enabled}
      role="switch"
    >
      <span
        className={`inline-block size-4 transform bg-white transition-transform ${
          enabled ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  );
}

export default function MetricsSnapshotCard() {
  const [cfg, setCfg] = useState(null);        // last-saved config
  const [draft, setDraft] = useState(null);    // editable draft
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/metrics/snapshot/config");
      setCfg(r.data);
      setDraft(JSON.parse(JSON.stringify(r.data)));
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading || !draft) {
    return (
      <div className="border border-[#27272A] bg-[#0A0A0A] p-6 text-zinc-500 text-sm" data-testid="metrics-snapshot-card">
        Loading weekly snapshot config…
      </div>
    );
  }

  const dirty = JSON.stringify(cfg) !== JSON.stringify(draft);
  const invalid = (draft.recipients || []).some((r) => r.trim() && !isEmail(r));
  const canSave = dirty && !invalid;

  const setRecipient = (idx, val) => setDraft((d) => {
    const arr = [...(d.recipients || [])];
    arr[idx] = val;
    return { ...d, recipients: arr };
  });
  const addRecipient = () => setDraft((d) => ({
    ...d,
    recipients: [...(d.recipients || []), ""],
  }));
  const removeRecipient = (idx) => setDraft((d) => ({
    ...d,
    recipients: (d.recipients || []).filter((_, i) => i !== idx),
  }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: !!draft.enabled,
        cadence: draft.cadence || "weekly",
        day_of_week: Number.isInteger(draft.day_of_week) ? draft.day_of_week : 0,
        send_hour_utc: Number.isInteger(draft.send_hour_utc) ? draft.send_hour_utc : 13,
        recipients: (draft.recipients || []).map((r) => r.trim().toLowerCase()).filter(Boolean),
      };
      const r = await api.put("/metrics/snapshot/config", payload);
      setCfg(r.data);
      setDraft(JSON.parse(JSON.stringify(r.data)));
      toast.success("Weekly snapshot settings saved");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!(draft.recipients || []).some(isEmail)) {
      toast.error("Add at least one valid recipient first");
      return;
    }
    if (dirty) {
      toast.error("Save your changes before sending a test");
      return;
    }
    setSending(true);
    try {
      const r = await api.post("/metrics/snapshot/send-now");
      if (r.data?.sent) {
        toast.success(`Test snapshot sent to ${r.data.recipients} recipient${r.data.recipients === 1 ? "" : "s"}`);
        load();
      } else if (r.data?.skipped) {
        toast.error(`Not sent: ${r.data.skipped}`);
      } else {
        toast.error(`Send failed: ${r.data?.reason || "unknown"}`);
      }
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="border border-[#27272A] bg-[#0A0A0A]" data-testid="metrics-snapshot-card">
      <div className="px-5 py-4 border-b border-[#27272A] flex items-center gap-2">
        <Mail className="size-4 text-zinc-400" />
        <div className="label-mute">Weekly metrics snapshot email</div>
      </div>
      <div className="px-5 py-3 text-xs text-zinc-500 border-b border-[#1c1c1e]">
        Automatically email a PDF of the <span className="text-zinc-300">Metrics tab</span>
        to leadership on your chosen cadence. All cadences fire
        <span className="text-zinc-300"> Monday morning at 08:00 ET</span> (13:00 UTC).
        The PDF matches what a manual export produces. Send a test now to preview.
      </div>

      {/* Cadence picker */}
      <div className="px-5 py-4 border-b border-[#1c1c1e]" data-testid="metrics-snapshot-cadence-section">
        <div className="flex items-center gap-2 mb-3">
          <Repeat className="size-3.5 text-zinc-400" />
          <div className="text-sm text-zinc-200 font-medium">Cadence</div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {CADENCE_OPTIONS.map((opt) => {
            const selected = (draft.cadence || "weekly") === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setDraft((d) => ({ ...d, cadence: opt.value }))}
                data-testid={`metrics-snapshot-cadence-${opt.value}`}
                className={`text-left border p-3 transition-colors ${
                  selected
                    ? "border-[#FF3B30] bg-[#1a0a0a]"
                    : "border-[#27272A] bg-[#141414] hover:border-[#3f3f42]"
                }`}
                aria-pressed={selected}
              >
                <div className={`text-sm font-medium ${selected ? "text-[#FF3B30]" : "text-zinc-200"}`}>
                  {opt.label}
                </div>
                <div className="text-[11px] text-zinc-500 mt-0.5">{opt.when}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Schedule — day + hour (UTC, browser-local hint) */}
      <div className="px-5 py-4 border-b border-[#1c1c1e]" data-testid="metrics-snapshot-schedule-section">
        <div className="flex items-center gap-2 mb-3">
          <Calendar className="size-3.5 text-zinc-400" />
          <div className="text-sm text-zinc-200 font-medium">Send day &amp; time</div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <div className="label-mute mb-1">Day of week</div>
            <select
              value={Number.isInteger(draft.day_of_week) ? draft.day_of_week : 0}
              onChange={(e) => setDraft((d) => ({ ...d, day_of_week: Number(e.target.value) }))}
              data-testid="metrics-snapshot-day"
              className="w-full bg-transparent border border-[#27272A] px-2 py-1.5 text-sm font-mono text-zinc-200 outline-none focus:border-[#FF3B30]"
            >
              {DAY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value} className="bg-[#0A0A0A]">{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <div className="label-mute mb-1">Hour (UTC)</div>
            <select
              value={Number.isInteger(draft.send_hour_utc) ? draft.send_hour_utc : 13}
              onChange={(e) => setDraft((d) => ({ ...d, send_hour_utc: Number(e.target.value) }))}
              data-testid="metrics-snapshot-hour"
              className="w-full bg-transparent border border-[#27272A] px-2 py-1.5 text-sm font-mono text-zinc-200 outline-none focus:border-[#FF3B30]"
            >
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={h} className="bg-[#0A0A0A]">
                  {String(h).padStart(2, "0")}:00
                </option>
              ))}
            </select>
            <div className="text-[11px] text-zinc-500 mt-1" data-testid="metrics-snapshot-local-hint">
              ≈ {localTimeForUtcHour(Number.isInteger(draft.send_hour_utc) ? draft.send_hour_utc : 13)} in your browser&apos;s timezone
            </div>
          </div>
        </div>
      </div>

      {/* Enable toggle */}
      <div className="px-5 py-4 flex items-center justify-between border-b border-[#1c1c1e]">
        <div>
          <div className="text-sm text-zinc-200 font-medium">Enabled</div>
          <div className="text-[11px] text-zinc-500 mt-0.5">
            When on, the snapshot fires on the cadence above and stamps <span className="font-mono text-zinc-300">last_sent_at</span> below.
          </div>
        </div>
        <Toggle
          enabled={!!draft.enabled}
          onChange={(v) => setDraft((d) => ({ ...d, enabled: v }))}
          testId="metrics-snapshot-enabled"
        />
      </div>

      {/* Recipients */}
      <div className="px-5 py-4 border-b border-[#1c1c1e]">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-sm text-zinc-200 font-medium">Recipients</div>
            <div className="text-[11px] text-zinc-500 mt-0.5">
              Up to 20 email addresses. Each recipient gets the PDF attached.
            </div>
          </div>
          <GhostButton
            onClick={addRecipient}
            disabled={(draft.recipients || []).length >= 20}
            data-testid="metrics-snapshot-add-recipient"
          >
            <Plus className="size-3.5 mr-1" /> Add
          </GhostButton>
        </div>
        {(draft.recipients || []).length === 0 ? (
          <div className="text-xs text-zinc-600 italic py-2" data-testid="metrics-snapshot-empty">
            No recipients yet — add at least one to enable the weekly send.
          </div>
        ) : (
          <div className="space-y-2">
            {(draft.recipients || []).map((r, idx) => {
              const bad = r.trim() && !isEmail(r);
              return (
                <div key={idx} className="flex items-center gap-2">
                  <input
                    type="email"
                    value={r}
                    onChange={(e) => setRecipient(idx, e.target.value)}
                    placeholder="leadership@company.com"
                    data-testid={`metrics-snapshot-recipient-${idx}`}
                    className={`flex-1 bg-transparent border px-2 py-1.5 text-sm font-mono text-zinc-200 outline-none focus:border-[#FF3B30] ${
                      bad ? "border-[#FF3B30]" : "border-[#27272A]"
                    }`}
                  />
                  <button
                    onClick={() => removeRecipient(idx)}
                    data-testid={`metrics-snapshot-remove-${idx}`}
                    className="text-zinc-500 hover:text-[#FF3B30] p-1"
                    aria-label="Remove recipient"
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </div>
              );
            })}
            {invalid && (
              <div className="text-[11px] text-[#FF3B30]" data-testid="metrics-snapshot-invalid">
                One or more recipients aren&apos;t valid email addresses.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Status + actions */}
      <div className="px-5 py-4 flex flex-wrap items-center gap-3">
        <div className="text-xs text-zinc-500 flex-1 min-w-[200px]" data-testid="metrics-snapshot-status">
          <Calendar className="size-3 inline mr-1" />
          {cfg.last_sent_at
            ? <>Last sent <span className="font-mono text-zinc-300">{new Date(cfg.last_sent_at).toLocaleString()}</span>{cfg.last_result ? <> · <span className="text-zinc-400">{cfg.last_result}</span></> : null}</>
            : <>Never sent yet.</>}
        </div>
        <GhostButton
          onClick={sendTest}
          disabled={sending || dirty || !(draft.recipients || []).some(isEmail)}
          data-testid="metrics-snapshot-send-test"
        >
          <Send className="size-3.5 mr-1" /> {sending ? "Sending…" : "Send test now"}
        </GhostButton>
        <ActionButton
          onClick={save}
          disabled={!canSave || saving}
          data-testid="metrics-snapshot-save"
        >
          {saving ? "Saving…" : "Save"}
        </ActionButton>
      </div>
    </div>
  );
}
